'use client';

import { Text, Heading, Box, Flex, Link } from '@chakra-ui/react';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { sanityImageBuilder } from '~/lib/sanity/imageBuilder';
import { LeadCaptureForm } from './LeadCaptureForm';

type Children = { children?: React.ReactNode };

export const portableTextComponents = {
  types: {
    leadCaptureForm: LeadCaptureForm,

    table: ({
      value,
    }: {
      value: {
        rows?: Array<{ cells?: string[] }>;
      };
    }) => {
      if (!value?.rows || value.rows.length === 0) {
        return null;
      }

      return (
        <Box my="16px" w="100%" overflowX="auto">
          <Box
            as="table"
            w="100%"
            sx={{
              borderCollapse: 'collapse',
              fontSize: '14px',
              'th, td': {
                border: '1px solid',
                borderColor: 'gray.200',
                padding: '8px 12px',
                textAlign: 'left',
              },
              th: {
                backgroundColor: 'gray.50',
                fontWeight: 'bold',
              },
            }}
          >
            <tbody>
              {value.rows.map((row, rowIndex) => (
                <Box as="tr" key={rowIndex}>
                  {row.cells?.map((cell, cellIndex) =>
                    rowIndex === 0 ? (
                      <Box as="th" key={cellIndex}>
                        {cell}
                      </Box>
                    ) : (
                      <Box as="td" key={cellIndex}>
                        {cell}
                      </Box>
                    )
                  )}
                </Box>
              ))}
            </tbody>
          </Box>
        </Box>
      );
    },

    image: ({ value }: { value: any }) => {
      if (!value?.asset) {
        return null;
      }

      const imageUrl = sanityImageBuilder(value).width(800).url();

      return (
        <Box
          my="16px"
          w="100%"
          position="relative"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Image
            src={imageUrl}
            width={800}
            height={600}
            style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
            alt={value.alt || ''}
          />
        </Box>
      );
    },

    recipe: ({ value }: { value: any }) => {
      if (!value) return null;

      return (
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap="24px"
          my="24px"
          p="16px"
          bg="gray.50"
          borderRadius="sm"
        >
          {value.image?.asset && (
            <Box
              flexBasis={{ base: '100%', md: '40%' }}
              position="relative"
              maxWidth={{ base: '100%', md: '300px' }}
            >
              <Image
                src={sanityImageBuilder(value.image).width(600).url()}
                width={300}
                height={300}
                style={{ objectFit: 'cover', borderRadius: '4px' }}
                alt={value.title || 'Recipe'}
              />
            </Box>
          )}

          <Box flexBasis={{ base: '100%', md: '60%' }}>
            {value.title && (
              <Heading as="h3" fontSize="20px" fontWeight="bold" mb="12px">
                {value.title}
              </Heading>
            )}

            {value.ingredients && (
              <Box mb="16px">
                <Heading as="h4" fontSize="16px" fontWeight="bold" mb="8px">
                  Ingredienser
                </Heading>
                <Box as="ul" listStyleType="none" m="0" p="0">
                  {value.ingredients.map((ingredient: string, idx: number) => (
                    <Box
                      key={idx}
                      as="li"
                      py="4px"
                      borderBottom="1px solid"
                      borderColor="gray.200"
                      fontSize="14px"
                    >
                      {ingredient}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {value.instructions && (
              <Box>
                <Heading as="h4" fontSize="16px" fontWeight="bold" mb="8px">
                  Så här gör du:
                </Heading>
                <Text fontSize="14px" lineHeight="1.6">
                  {value.instructions}
                </Text>
              </Box>
            )}
          </Box>
        </Flex>
      );
    },

    imageWithText: ({ value }: { value: any }) => {
      if (!value?.image?.asset || !value?.content) {
        return null;
      }

      const imageUrl = sanityImageBuilder(value.image).width(900).url();
      const isRightPlacement =
        value.placement?.includes('right') || !value.placement;
      const isRecipeStyle =
        value.isRecipe ||
        (value.content &&
          value.content.some(
            (block: any) =>
              block.children &&
              block.children.some(
                (child: any) =>
                  child.text &&
                  (child.text.includes('Ingredienser') ||
                    child.text.includes('gör du') ||
                    child.text.includes('g kallrökt lax'))
              )
          ));

      return (
        <Box
          className={`col-xs-12 block-container-image__${
            isRightPlacement ? 'right' : 'left'
          }-narrow`}
          my={isRecipeStyle ? '24px' : '16px'}
          border={isRecipeStyle ? '1px solid' : 'none'}
          borderColor={isRecipeStyle ? 'gray.200' : 'transparent'}
          borderRadius={isRecipeStyle ? 'sm' : 'none'}
          overflow="hidden"
        >
          <Flex
            direction={{
              base: 'column',
              md: isRightPlacement ? 'row-reverse' : 'row',
            }}
            gap={isRecipeStyle ? '8px' : '16px'}
            align="flex-start"
            justifyContent="center"
          >
            <Box
              className="image-block-column image-block-image"
              flexBasis={{ base: '100%', md: isRecipeStyle ? '30%' : '40%' }}
              w="100%"
              p={isRecipeStyle ? '16px' : '0'}
              display="flex"
              justifyContent="center"
            >
              <Box
                position="relative"
                width="100%"
                display="flex"
                justifyContent="center"
              >
                <Image
                  src={imageUrl}
                  alt={value.alt || ''}
                  width={900}
                  height={isRecipeStyle ? 500 : 600}
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover',
                    borderRadius: isRecipeStyle ? '4px' : 'none',
                  }}
                  loading="lazy"
                />
                {value.byline && (
                  <Box
                    className="image-byline"
                    fontSize="sm"
                    fontStyle="italic"
                    mt="2"
                    opacity="0.8"
                    textAlign="center"
                    width="100%"
                  >
                    {value.byline}
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              className="image-block-column image-block-text"
              flexBasis={{ base: '100%', md: isRecipeStyle ? '70%' : '60%' }}
              p={isRecipeStyle ? '16px' : '0'}
            >
              <PortableText
                value={value.content}
                components={portableTextComponents}
              />
            </Box>
          </Flex>
        </Box>
      );
    },
  },
  block: {
    normal: ({ children }: Children) => {
      // Extract text to check for indentation
      const text =
        typeof children === 'string'
          ? children
          : Array.isArray(children)
          ? children.map((c) => (typeof c === 'string' ? c : '')).join('')
          : '';

      // Count leading spaces to determine indentation level
      const leadingSpaces = text.match(/^(\s*)/)?.[1]?.length || 0;
      const trimmedText = text.trimStart();
      const isBulletItem =
        trimmedText.startsWith('• ') || trimmedText.startsWith('- ');
      const isListItem = isBulletItem || /^\d+\.\s/.test(trimmedText);

      // Determine indentation: 4 spaces = 24px, 8 spaces = 48px
      let paddingLeft = '0';
      if (leadingSpaces >= 8) {
        paddingLeft = '48px';
      } else if (leadingSpaces >= 4) {
        paddingLeft = '24px';
      }

      return (
        <Text
          opacity="0.9"
          py={isListItem ? '3px' : '6px'}
          lineHeight="1.6"
          fontSize="16px"
          maxWidth="100%"
          fontFamily="system-ui, -apple-system, sans-serif"
          pl={paddingLeft}
          sx={{
            a: {
              fontWeight: 'bold',
              marginLeft: '5px',
              display: 'inline-block',
              textDecoration: 'underline',
            },
            'ul, ol': {
              paddingLeft: '20px',
              marginBottom: '16px',
              marginTop: '8px',
            },
            li: {
              marginBottom: '6px',
            },
          }}
        >
          {children}
        </Text>
      );
    },
    h1: ({ children }: Children) => (
      <Heading
        fontSize="32px"
        fontWeight="bold"
        fontFamily="system-ui, -apple-system, sans-serif"
        mt="32px"
        mb="16px"
        lineHeight="1.2"
      >
        {children}
      </Heading>
    ),
    h2: ({ children }: Children) => (
      <Heading
        fontSize="28px"
        fontWeight="bold"
        fontFamily="system-ui, -apple-system, sans-serif"
        mt="28px"
        mb="14px"
        lineHeight="1.3"
      >
        {children}
      </Heading>
    ),
    h3: ({ children }: Children) => (
      <Heading
        fontSize="24px"
        fontWeight="bold"
        fontFamily="system-ui, -apple-system, sans-serif"
        mt="24px"
        mb="12px"
        lineHeight="1.3"
      >
        {children}
      </Heading>
    ),
    h4: ({ children }: Children) => (
      <Heading
        fontSize="20px"
        fontWeight="bold"
        fontFamily="system-ui, -apple-system, sans-serif"
        mt="20px"
        mb="10px"
        lineHeight="1.4"
      >
        {children}
      </Heading>
    ),
    h5: ({ children }: Children) => (
      <Heading
        fontSize="18px"
        fontWeight="bold"
        fontFamily="system-ui, -apple-system, sans-serif"
        mt="16px"
        mb="8px"
        lineHeight="1.4"
      >
        {children}
      </Heading>
    ),
    h6: ({ children }: Children) => (
      <Heading
        fontSize="16px"
        fontWeight="bold"
        fontFamily="system-ui, -apple-system, sans-serif"
        mt="12px"
        mb="6px"
        lineHeight="1.4"
      >
        {children}
      </Heading>
    ),
  },
  marks: {
    link: ({ children, value }: { children: React.ReactNode; value: any }) => {
      const rel = !value.href.startsWith('/')
        ? 'noreferrer noopener'
        : undefined;
      return (
        <Link
          href={value.href}
          rel={rel}
          fontWeight="bold"
          marginLeft="5px"
          textDecoration="underline"
          isExternal={!value.href.startsWith('/')}
        >
          {children}
        </Link>
      );
    },
  },
  listItem: {
    bullet: ({ children }: Children) => (
      <Box as="li" display="flex" ml="20px" mb="4px">
        <Text as="span" mr="8px">
          •
        </Text>
        <Text>{children}</Text>
      </Box>
    ),
    number: ({ children }: { children: React.ReactNode }) => (
      <Box as="li" display="flex" ml="20px" mb="4px">
        <Text>{children}</Text>
      </Box>
    ),
  },
} as TODO;
